import { useEffect, useState } from "react";
import { StatCard, AgingBar } from "@/components/DashboardWidgets";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import api from "@/lib/api";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { formatMoney } from "@/components/receivables/shared";

export function OverviewTab({ onOpen }) {
  const [data, setData] = useState(null);
  const { activeRail, filterRails } = useCurrencyRail();
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/receivables/summary");
        setData(data);
      } catch (err) {
        console.warn("[Receivables] summary failed:", err?.message);
      }
    })();
  }, []);
  if (!data) return <div className="text-sm text-[var(--muted-text)]">Loading…</div>;
  // When ALL: show mixed dashboard (labeled) + per-rail cards. When USD/NGN: only that rail's data.
  const rails = filterRails(data.by_currency || []);
  const showMixed = activeRail === "ALL";
  return (
    <div className="space-y-6" data-testid="ar-overview">
      {showMixed && (
        <>
          {/* FX-consolidated card (iter 68) — visible when the sidebar
              "Convert to USD" toggle is ON AND 2+ rails have activity. Sums
              outstanding / overdue / paid-mtd / invoiced-YTD across rails
              at live FX rates so the header numbers actually mean something. */}
          <ConsolidatedRailCard
            rails={(data.by_currency || []).map((r) => ({
              currency: r.currency,
              outstanding: r.totals?.outstanding ?? 0,
              overdue: r.totals?.overdue ?? 0,
              paid_this_month: r.totals?.paid_this_month ?? 0,
              invoiced_ytd: r.totals?.invoiced_ytd ?? 0,
            }))}
            testId="ar-consolidated"
            subtitle="live FX across every AR rail"
            fields={[
              { key: "outstanding",     label: "Outstanding",     color: "text-[#2F4F4F]" },
              { key: "overdue",         label: "Overdue",         color: "text-[#B4283A]" },
              { key: "paid_this_month", label: "Paid this month", color: "text-[#4C956C]" },
              { key: "invoiced_ytd",    label: "Invoiced YTD",    color: "text-[#E07A5F]" },
            ]}
          />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Outstanding · native sum" value={data.totals.outstanding} currency=""
                      sublabel={`${data.counts.outstanding} invoices · mixed currencies`} tone="brand"
                      testid="ar-stat-outstanding" />
            <StatCard label="Overdue · native sum" value={data.totals.overdue} currency=""
                      sublabel={`${data.counts.overdue} past due · mixed currencies`}
                      tone={data.totals.overdue > 0 ? "danger" : "default"}
                      testid="ar-stat-overdue" />
            <StatCard label="Paid this month · native sum" value={data.totals.paid_this_month} currency=""
                      sublabel="Mixed currencies · pick a rail to isolate"
                      testid="ar-stat-paid-mtd" />
            <StatCard label="Invoiced YTD · native sum" value={data.totals.invoiced_ytd} currency=""
                      sublabel={`${data.counts.total_invoices} invoices · mixed currencies`}
                      testid="ar-stat-invoiced-ytd" />
          </div>
        </>
      )}

      {/* Per-currency rail cards — only these show when a specific rail is picked */}
      {rails.map((rail) => (
        <div key={`ov-rail-${rail.currency}`} data-testid={`ar-overview-rail-${rail.currency}`} className="space-y-3">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">
            <span className="px-1.5 py-0.5 rounded font-mono text-[10px] text-[#FDFCF8]"
                  style={{ backgroundColor: rail.currency === "USD" ? "#1E5A96" : "#4C956C" }}>{rail.currency}</span>
            <span>rail</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Outstanding" value={rail.totals.outstanding} currency={rail.currency}
                      sublabel={`${rail.counts.outstanding} invoices`} tone="brand"
                      testid={`ar-stat-outstanding-${rail.currency}`} />
            <StatCard label="Overdue" value={rail.totals.overdue} currency={rail.currency}
                      sublabel={`${rail.counts.overdue} past due`}
                      tone={rail.totals.overdue > 0 ? "danger" : "default"}
                      testid={`ar-stat-overdue-${rail.currency}`} />
            <StatCard label="Paid this month" value={rail.totals.paid_this_month} currency={rail.currency}
                      sublabel="Cash in from customers"
                      testid={`ar-stat-paid-mtd-${rail.currency}`} />
            <StatCard label="Invoiced YTD" value={rail.totals.invoiced_ytd} currency={rail.currency}
                      sublabel="This year"
                      testid={`ar-stat-invoiced-ytd-${rail.currency}`} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card-flat p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Aging</div>
                <button onClick={() => onOpen("aging")} className="text-xs text-[var(--brand)] hover:underline"
                        data-testid={`ar-view-aging-${rail.currency}`}>View aged AR →</button>
              </div>
              <AgingBar buckets={rail.aging} currency={rail.currency} testidPrefix={`ar-aging-${rail.currency}`} />
            </div>
            <div className="card-flat p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Top debtors</div>
                <button onClick={() => onOpen("invoices")} className="text-xs text-[var(--brand)] hover:underline">View all invoices →</button>
              </div>
              {rail.top_debtors.length === 0 ? (
                <div className="text-sm text-[var(--muted-text)] py-4">No debtors — everyone's paid up.</div>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {rail.top_debtors.map((d) => (
                      <tr key={`${rail.currency}-${d.customer}`} className="border-b border-[var(--divider)] last:border-none">
                        <td className="py-2 truncate">{d.customer}</td>
                        <td className="py-2 text-right font-mono-data font-medium">{formatMoney(d.balance, rail.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------- Customers ----------------
